import { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Alert, MenuItem, TextField, CircularProgress, Box, Typography,
} from "@mui/material";
import { getAvailableRooms, checkInReservation } from "./ReservationApi";

export default function ReservationCheckInDialog({ open, reservation, onClose, onCheckedIn }) {
    const [availableRooms, setAvailableRooms] = useState([]);
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !reservation) return;
        setLoading(true);
        setError(null);
        getAvailableRooms(reservation.requestedCheckin, reservation.requestedCheckout)
            .then((rooms) => {
                setAvailableRooms(rooms);
                // pre-assigned room still free? default to it. otherwise leave for staff to pick.
                const preAssignedStillFree = reservation.roomId && rooms.some((r) => r.id === reservation.roomId);
                setSelectedRoomId(preAssignedStillFree ? reservation.roomId : (rooms[0]?.id ?? null));
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [open, reservation]);

    async function handleConfirm() {
        if (!selectedRoomId) return;
        setSubmitting(true);
        setError(null);
        try {
            const result = await checkInReservation(reservation.id, {
                roomId: selectedRoomId,
                actualCheckin: new Date().toISOString().slice(0, 10),
            });
            onCheckedIn?.(result);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    }

    if (!reservation) return null;

    const roomWasReassigned = reservation.roomId && selectedRoomId !== reservation.roomId;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Check in — {reservation.guestName || `Reservation #${reservation.id}`}</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        {availableRooms.length === 0 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                No rooms are currently available for these dates.
                            </Alert>
                        )}

                        {roomWasReassigned && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                The pre-assigned room is no longer available — please pick another.
                            </Alert>
                        )}

                        <TextField
                            select label="Room" fullWidth
                            value={selectedRoomId ?? ""}
                            onChange={(e) => setSelectedRoomId(Number(e.target.value))}
                            disabled={availableRooms.length === 0}
                        >
                            {availableRooms.map((r) => (
                                <MenuItem key={r.id} value={r.id}>
                                    Room {r.alias} — {r.bedType}, {r.size}
                                </MenuItem>
                            ))}
                        </TextField>

                        {selectedRoomId && !roomWasReassigned && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                Confirmed available for {reservation.requestedCheckin} – {reservation.requestedCheckout}
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={loading || submitting || !selectedRoomId}
                >
                    {submitting ? "Checking in…" : "Confirm check-in"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}